import React from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useNavigateWithScrollMemory } from '../../hooks/useNavigateWithScrollMemory';
import AlbumCover from '../common/AlbumCover';
import AlbumTitleLink from '../common/AlbumTitleLink';
import ArtistLink from '../common/ArtistLink';
import RatingReadOnly from '../common/RatingReadOnly';
import ReviewCount from '../common/ReviewCount';
import BookmarkCount from '../common/BookmarkCount';
import theme from '../../theme';

/**
 * AlbumHorizontalCard - 앨범 가로형 카드 컴포넌트
 *
 * 마이페이지 저장한 게시물 (xs) 등에서 사용
 * +-------+-------------------+
 * | image | title             |
 * |       | artist            |
 * |       | rating            |
 * |       | date | RC BC  [B] |
 * +-------+-------------------+
 */
const AlbumHorizontalCard = ({
  sourceId,
  albumSource,
  slug,
  imageUrl,
  albumName,
  artistId,
  artistDbId,
  artistSource,
  artistName,
  artistSlug,
  albumAvgRating = 0,
  reviewCount,
  bookmarkCount,
  releaseDate,
  contentRating = 'CLEAN',
  isStarred = false,
  actionButton,
  imageWidth = '30%',
  imageMinWidth = 96,
  imageMaxWidth,
  cardAlignItems = 'flex-start',
  referrerType,
}) => {
  const navigateWithScrollMemory = useNavigateWithScrollMemory();
  const albumUrl = slug ? `/reviews/${slug}` : `/reviews/${albumSource?.toLowerCase()}/${sourceId}`;

  const handleClick = (e) => {
    if (!e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      navigateWithScrollMemory(albumUrl, referrerType ? { state: { referrerType } } : undefined);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: cardAlignItems,
        gap: 1.25,
      }}
    >
      {/* 좌측 이미지 */}
      <Box
        component={RouterLink}
        to={albumUrl}
        state={referrerType ? { referrerType } : undefined}
        onClick={handleClick}
        sx={{
          display: 'block',
          width: imageWidth,
          minWidth: imageMinWidth,
          ...(imageMaxWidth ? { maxWidth: imageMaxWidth } : {}),
          flexShrink: 0,
        }}
      >
        <AlbumCover
          imageUrl={imageUrl}
          altText={albumName}
          isStarred={isStarred}
          sx={{
            aspectRatio: '1 / 1',
            objectFit: 'cover',
            width: '100%',
          }}
        />
      </Box>

      {/* 우측 정보 영역 */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <AlbumTitleLink
          title={albumName}
          to={albumUrl}
          contentRating={contentRating}
          sx={{ fontSize: 14 }}
          iconSx={{ width: '12px', height: '12px' }}
        />
        {artistName && (
          <ArtistLink
            artistName={artistName}
            artistSource={artistSource}
            artistSourceId={artistId}
            artistSlug={artistSlug}
            artistDbId={artistDbId}
            sx={{
              fontSize: 13,
              color: theme.palette.black[4],
              WebkitLineClamp: 1,
              mt: 1,
            }}
          />
        )}

        {/* 하단 정보 + actionButton */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            mt: 1.5,
          }}
        >
          <Box>
            <RatingReadOnly value={albumAvgRating} variant="fullResponsive" />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
              {releaseDate && (
                <>
                  <Typography
                    fontSize={12}
                    color={theme.palette.black[4]}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {releaseDate}
                  </Typography>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      height: 8,
                      alignSelf: 'center',
                      backgroundColor: theme.palette.grey.line,
                    }}
                  />
                </>
              )}
              <Stack direction="row" spacing={1} alignItems="center">
                <ReviewCount reviewCount={reviewCount} />
                <BookmarkCount bookmarkCount={bookmarkCount} />
              </Stack>
            </Stack>
          </Box>

          {/* 우측: actionButton */}
          {actionButton}
        </Box>
      </Box>
    </Box>
  );
};

export default AlbumHorizontalCard;
